import React, { useEffect, useMemo, useState, useRef } from 'react';
import SimpleBar from 'simplebar-react';

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

import BitaCheckbox from '../../../../../../components/BitaCheckbox/BitaCheckbox';
import BitaInput from '../../../../../../components/BitaInput/BitaInput';
import BitaButton from '../../../../../../bitaComponents/BitaButton/BitaButton';
import PaginationComponent from '../../../../../../components/Analyzer/PaginationComponent';
import { TableCell } from './TableCell';

import { getColumnWidth } from '../../../../utils/index';

import styles from './TableList.module.scss';

const TableList = ({
  data,
  headerType = 'instrument',
  height = '300px',
  onChangeSelect,
  headers = {},
  checkedBasketsIds,
  selectedBasketsCount = 0,
  handleClearSelection,
}) => {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pagination, setPagination] = useState({ pageSize: 1000, pageIndex: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchInstruments, setSearchInstruments] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [maxPage, setMaxPage] = useState();
  const [page, setPage] = useState(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    const length = filteredData?.length > 0 ? filteredData.length : data?.length;
    setMaxPage(Math.ceil(length / 10));
  }, [filteredData, data]);

  // Debounce search input to avoid excessive filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInstruments);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInstruments]);

  // Enhanced search handler
  const handleSearchChange = value => setSearchInstruments(value);

  // Clear search handler
  const handleClearSearch = () => {
    setSearchInstruments('');
    setDebouncedSearch('');
    setPage(0);
  };

  useEffect(() => {
    isMountedRef.current = true;

    /**
     * Temporary timeout to emulate API response time.
     * Sets the table data and updates the loading state after 1 second.
     */
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        setTableData(data);
        setSelectedRows([]);
        setIsLoading(data?.length === 0);
      }
    }, 1000);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
    };
  }, [data]);

  useEffect(() => {
    if (checkedBasketsIds.length === 0) {
      setSelectedRows([]);
    }
  }, [checkedBasketsIds]);

  const handleCheckboxChange = (rowId, checked) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(rowId);
    } else {
      newSelectedRows.delete(rowId);
    }
    const newSelectedArray = Array.from(newSelectedRows);
    setSelectedRows(newSelectedArray);
    onChangeSelect(newSelectedArray);
  };

  const columns = useMemo(
    () => [
      ...Object.keys(headers).map(key => ({
        header: key.charAt(0).toUpperCase() + key.slice(1),
        accessorKey: key,
        cell: props => <TableCell {...props} headerType={headerType} />,
      })),
      ...[
        {
          header: '',
          accessorKey: 'select',
          cell: row => {
            const rowId = row?.row?.original?.id;

            return (
              <BitaCheckbox
                checked={selectedRows.includes(rowId)}
                handleChange={checked => {
                  handleCheckboxChange(rowId, checked);
                }}
              />
            );
          },
        },
      ],
    ],
    [headerType, selectedRows],
  );

  const table = useReactTable({
    data: tableData || [],
    columns,
    state: {
      pagination,
      sorting,
      globalFilter: debouncedSearch,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setDebouncedSearch,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setPage(0);
    setFilteredData(table.getRowModel().rows);
  }, [debouncedSearch]);

  const renderHeader = header => {
    const headerText =
      flexRender(header.column.columnDef.header, header.getContext())?.toString() || '';
    const headerColumns = headers;
    return headerColumns[header.column.id] ?? headerText;
  };

  const setCurrentPage = value => {
    setPage(value - 1);
  };
  return (
    <div className={styles.contentTotalWidth}>
      <div className={styles.controlsRow}>
        <BitaInput
          placeholder="Search baskets..."
          value={searchInstruments}
          handleChange={handleSearchChange}
          inputContainerStyle={styles.searchInputContainer}
        />
        {searchInstruments && (
          <button onClick={handleClearSearch} className={styles.clearButton} title="Clear search">
            Clear
          </button>
        )}
        <span className={styles.countText}>
          {filteredData?.length} of {tableData?.length || 0} instruments
        </span>
      </div>

      {isLoading ? (
        <div className={styles.contentLoader} />
      ) : (
        <div className={styles.tableContainer}>
          <div className={styles.contentTable}>
            <SimpleBar className={styles.tableScroll} style={{ maxHeight: height }}>
              <table className={`${styles.contentTotalWidth} ${styles.tableElement}`}>
                <thead className={styles.tableHead}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => {
                        return (
                          <th
                            key={header.id}
                            className={styles.tableHeaderCell}
                            style={{ width: getColumnWidth(header.column.id) }}
                          >
                            <div
                              className={styles.tableHeaderContent}
                              onClick={
                                header.column.id !== 'select'
                                  ? header.column.getToggleSortingHandler()
                                  : () => {}
                              }
                            >
                              {renderHeader(header)}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>

                <tbody>
                  {table
                    .getRowModel()
                    .rows?.slice(page * 10, (page + 1) * 10)
                    ?.map(row => (
                      <tr key={row.id} className={styles.tableRow}>
                        {row.getVisibleCells().map((cell, index) => {
                          return (
                            <td key={cell.id} className={styles.tdM}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </SimpleBar>
            <div className={styles.paginationContainer}>
              <PaginationComponent totalPages={maxPage} onClick={setCurrentPage} />
              {selectedBasketsCount > 0 && (
                <div className={styles.clearSelectionContainer}>
                  <div className={styles.paginationCount}>
                    {selectedBasketsCount} basket(s) selected
                  </div>
                  <BitaButton primaryWhite width="120px" onClick={handleClearSelection}>
                    Clear Selection
                  </BitaButton>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={styles.contentTotalWidth}>
        <div className="pagination custom-edit-button" />
      </div>
    </div>
  );
};

export default TableList;
